import { Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CreateSaleForm } from '@/features/sales/components/create-sale-form';
import { useCreateSale } from '@/features/sales/api/create-sale';
import { createRoundOption } from '@/features/sales/utils/round-options';
import { useSalesRoundsSuspense } from '@/features/sales-rounds/api/list-sales-round';
import { listGroupsQueryOptions } from '@/features/groups/api/list-groups';
import { Spinner } from '@/shared/components/ui/spinner';
import { CreateSaleInput } from '@/features/sales/api/create-sale';
import { getGroupQueryOptions } from '../../api/get-group';
import { useOnboardingPrefs } from '../../stores/onboarding-prefs.store';
import { onboardingMutationKey } from '../../hooks/use-onboarding-progress';
import { OnboardingTaskKey } from '@/types/api/group/onboarding-task';

export function CreateSaleTask({ groupId }: { groupId: string }) {
  return (
    <Suspense fallback={<Spinner size="sm" />}>
      <CreateSaleTaskForm groupId={groupId} />
    </Suspense>
  );
}

function CreateSaleTaskForm({ groupId }: { groupId: string }) {
  const queryClient = useQueryClient();
  const prefs = useOnboardingPrefs(groupId);
  const { data: rounds } = useSalesRoundsSuspense({});
  // Stay on the onboarding page: it shows the celebration right away
  const mutation = useCreateSale({
    mutationConfig: {
      mutationKey: onboardingMutationKey(groupId, OnboardingTaskKey.CreateSale),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: listGroupsQueryOptions().queryKey });
        await queryClient.invalidateQueries({ queryKey: getGroupQueryOptions(groupId).queryKey });
      },
    },
  });

  if (rounds.length <= 0) {
    return <p className="text-sm">Inga försäljningsrundor öppna just nu. Titta in igen snart!</p>;
  }

  // Start from the website's profit calculator values when the leader came from there
  const defaultValues: Partial<CreateSaleInput> = {
    ...(prefs.participantCount && { expectedParticipationCount: prefs.participantCount }),
    ...(prefs.itemsPerParticipant && { participationItemCountTarget: prefs.itemsPerParticipant }),
  };

  return (
    <CreateSaleForm
      roundOptions={rounds.map(createRoundOption)}
      defaultValues={defaultValues as CreateSaleInput}
      className="grid grid-cols-1 md:grid-cols-2 gap-6 md:[&>*:last-child]:col-start-2"
      onSubmit={(data) => mutation.mutateAsync({ data })}
    />
  );
}
